import { Button, Input, useDebounceCallback } from "@devhop/ui";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { XIcon } from "lucide-react";
import { useEffect, useState } from "react";
import type {
  FilterConditionDTO,
  ShopStaffIndexSearchDTO,
} from "@/shared/contracts/base";
import {
  findCondition,
  removeConditions,
  upsertCondition,
  upsertOrGroup,
} from "@/shared/contracts/query-helpers";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";

const FILTER_ALL = "__all__";

type ShopRoleOption = { id: string; name: string };

type ShopStaffFilterProps = {
  shopRoles: ShopRoleOption[];
};

export function ShopStaffFilter({ shopRoles }: ShopStaffFilterProps) {
  const params = useParams({ strict: false });
  const shopId = params?.shopId as string | undefined;
  const nav = useNavigate({ from: "/app/shops/$shopId/staff" });
  const search = (useSearch({
    from: "/app/shops/$shopId/staff",
    strict: false,
  }) ?? {}) as ShopStaffIndexSearchDTO;
  const filters = (search.filters as FilterConditionDTO[] | undefined) ?? [];

  const nameFilter = findCondition(filters, "userName");
  const emailFilter = findCondition(filters, "userEmail");
  const shopRoleIdFilter = findCondition(filters, "shopRoleId");
  const isActiveFilter = findCondition(filters, "isActive");

  const [searchValue, setSearchValue] = useState<string>(
    (nameFilter?.value as string) || (emailFilter?.value as string) || "",
  );

  useEffect(() => {
    const nameVal = nameFilter?.value as string;
    const emailVal = emailFilter?.value as string;
    setSearchValue(nameVal || emailVal || "");
  }, [nameFilter?.value, emailFilter?.value]);

  const debounced = useDebounceCallback((val: string) => {
    setSearchValue(val);
    let nextFilters: FilterConditionDTO[] | undefined = filters;
    if (val) {
      nextFilters = upsertOrGroup(filters, [
        { field: "userName", op: "contains", value: val },
        { field: "userEmail", op: "contains", value: val },
      ]);
    } else {
      nextFilters = removeConditions(filters, "userName");
      nextFilters = removeConditions(nextFilters, "userEmail");
    }
    nav({
      to: "/app/shops/$shopId/staff",
      params: { shopId: shopId ?? "" },
      search: {
        ...search,
        offset: 0,
        filters: nextFilters?.length ? nextFilters : undefined,
      },
    });
  }, 400);

  const shopRoleIdValue =
    shopRoleIdFilter?.value != null
      ? String(shopRoleIdFilter.value)
      : FILTER_ALL;
  const isActiveValue =
    typeof isActiveFilter?.value === "boolean"
      ? String(isActiveFilter.value)
      : FILTER_ALL;

  const hasFilter =
    !!nameFilter?.value ||
    !!emailFilter?.value ||
    !!shopRoleIdFilter?.value ||
    typeof isActiveFilter?.value === "boolean";

  if (!shopId) return null;

  return (
    <div
      className="mb-2 flex flex-col gap-2 px-2"
      data-tourid="shop-staff-filter"
    >
      <div className="flex items-center justify-between gap-4 max-sm:flex-col">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          <Input
            placeholder="ຄົ້ນຫາພະນັກງານ (ຊື່ / ອີເມວ)..."
            defaultValue={searchValue}
            onChange={(e) => debounced(e.target.value)}
            className="h-8 sm:max-w-2xs"
          />
          <SimpleSelect
            value={shopRoleIdValue}
            onValueChange={(val) => {
              let nextFilters: FilterConditionDTO[] | undefined = filters;
              if (!val || val === FILTER_ALL) {
                nextFilters = removeConditions(filters, "shopRoleId");
              } else {
                nextFilters = upsertCondition(filters, {
                  field: "shopRoleId",
                  op: "eq",
                  value: val,
                });
              }
              nav({
                to: "/app/shops/$shopId/staff",
                params: { shopId },
                search: {
                  ...search,
                  offset: 0,
                  filters: nextFilters?.length ? nextFilters : undefined,
                },
              });
            }}
            placeholder="ບົດບາດໃນຮ້ານ"
            className="h-8 w-full sm:w-40"
            options={[
              { value: FILTER_ALL, label: "ທັງໝົດ" },
              ...shopRoles.map((r) => ({ value: r.id, label: r.name })),
            ]}
          />
          <SimpleSelect
            value={isActiveValue}
            onValueChange={(val) => {
              let nextFilters: FilterConditionDTO[] | undefined = filters;
              if (!val || val === FILTER_ALL) {
                nextFilters = removeConditions(filters, "isActive");
              } else {
                nextFilters = upsertCondition(filters, {
                  field: "isActive",
                  op: "eq",
                  value: val === "true",
                });
              }
              nav({
                to: "/app/shops/$shopId/staff",
                params: { shopId },
                search: {
                  ...search,
                  offset: 0,
                  filters: nextFilters?.length ? nextFilters : undefined,
                },
              });
            }}
            placeholder="ສະຖານະ"
            className="h-8 w-full sm:w-32"
            options={[
              { value: FILTER_ALL, label: "ທັງໝົດ" },
              { value: "true", label: "ໃຊ້ງານຢູ່" },
              { value: "false", label: "ບໍ່ໃຊ້ງານ" },
            ]}
          />
        </div>
        {hasFilter && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              nav({
                to: "/app/shops/$shopId/staff",
                params: { shopId },
                search: { ...search, filters: undefined, offset: 0 },
              })
            }
            className="max-sm:w-full"
          >
            <XIcon className="h-4 w-4" /> ລ້າງ
          </Button>
        )}
      </div>
    </div>
  );
}
